import { forwardRef, useCallback, useImperativeHandle, useMemo, useReducer } from "react";
import { Box, Button, Chip, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { DemoUploadCard } from "./DemoUploadCard";
import { getDemoState } from "./demoStates";
import {
  NETWORKS,
  NETWORK_LABEL,
  availableEvents,
  deriveDemoKey,
  initialPlaygroundState,
  playgroundReducer,
} from "./playgroundModel";

/**
 * "Try it" sandbox shown in the tutorial popup. Combines a connection toggle,
 * a row of events and the interactive demo card. Nothing here touches a real
 * upload. Exposes `dispatch` through the ref so a running tour can push the
 * playground into the state a step talks about.
 */

const EVENTS = [
  { key: "reload", label: "Reload page", tour: "pg-event-reload" },
  { key: "otherDevice", label: "Open on another device", tour: "pg-event-other-device" },
  { key: "longOutage", label: "Stay offline a while", tour: "pg-event-long-outage" },
  { key: "finish", label: "Finish upload", tour: "pg-event-finish" },
  { key: "reset", label: "Reset", tour: "pg-event-reset" },
];

const chipSx = {
  height: 22,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  backgroundColor: "rgba(245,199,84,0.18)",
  color: "#F5C754",
  borderRadius: "6px",
};

export const UploadPlayground = forwardRef(({ compact = false, showExplain = true }, ref) => {
  const [state, dispatch] = useReducer(playgroundReducer, undefined, initialPlaygroundState);
  const demoKey = deriveDemoKey(state);
  const demo = useMemo(() => getDemoState(demoKey), [demoKey]);
  const events = availableEvents(state);

  useImperativeHandle(ref, () => ({ dispatch, getState: () => state }), [state]);

  const onCardAction = useCallback((type) => dispatch({ type }), []);

  return (
    <Box data-testid="upload-playground" data-tour="playground">
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(247,250,250,0.6)" }}>
          Connection
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={state.network}
          onChange={(_e, v) => v && dispatch({ type: "network", network: v })}
          data-tour="pg-network"
          data-testid="pg-network"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              px: 1.5,
              py: 0.4,
              color: "rgba(247,250,250,0.8)",
              borderColor: "rgba(255,255,255,0.18)",
            },
            "& .Mui-selected": { backgroundColor: "#5D62B6 !important", color: "#fff !important" },
          }}
        >
          {NETWORKS.map((n) => (
            <ToggleButton key={n} value={n} data-tour={`pg-network-${n}`} data-testid={`pg-network-${n}`}>
              {NETWORK_LABEL[n]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2 }} data-tour="pg-events">
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(247,250,250,0.6)", mr: 0.5 }}>
          Events
        </Typography>
        {EVENTS.map((ev) => (
          <Button
            key={ev.key}
            size="small"
            variant="outlined"
            disabled={!events[ev.key]}
            onClick={() => dispatch({ type: ev.key })}
            data-tour={ev.tour}
            data-testid={ev.tour}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              color: "#F7FAFA",
              borderColor: "rgba(255,255,255,0.28)",
              "&.Mui-disabled": { color: "rgba(247,250,250,0.35)", borderColor: "rgba(255,255,255,0.1)" },
            }}
          >
            {ev.label}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          // Without the explanation column the card keeps its real width and the
          // free space on the right is where the tour popover goes.
          gridTemplateColumns: compact
            ? "1fr"
            : { xs: "1fr", md: showExplain ? "minmax(0, 1fr) 260px" : "minmax(0, 560px) 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Box
          data-tour="pg-card-frame"
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: "rgba(41,56,77,0.7)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <DemoUploadCard state={demoKey} interactive onAction={onCardAction} />
        </Box>
        {showExplain && (
        <Box data-testid="pg-explain" data-tour="pg-explain">
          <Chip size="small" label={demo.chip} sx={{ ...chipSx, mb: 1 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#F7FAFA", mb: 0.5 }}>{demo.label}</Typography>
          <Typography sx={{ fontSize: 13.5, color: "rgba(247,250,250,0.82)", lineHeight: 1.55 }}>
            {demo.explain}
          </Typography>
        </Box>
        )}
      </Box>
    </Box>
  );
});

UploadPlayground.displayName = "UploadPlayground";
